import React, { useState } from "react";
import "./MessageDetail.css";
import toast from "react-hot-toast";

function AuthImage({ path, token }) {
  const [src, setSrc] = useState(null);

  React.useEffect(() => {
    if (!path || !token) return;
    let objectUrl;
    fetch(`/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error("Failed to load image");
        return res.blob();
      })
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [path, token]);

  if (!src) return null;
  return <img src={src} alt="" loading="lazy" className="thumbnail" />;
}

function MessageDetail({ message, onUpdate, onClear }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const token = localStorage.getItem("token");

  if (!message) {
    return (
      <div className="message-detail empty">
        <p>Select a message to view</p>
      </div>
    );
  }

  const handleToggleComplete = async () => {
    if (!token) { toast.error("You must be logged in"); return; }
    const endpoint = message.completed
      ? `/api/messages/${message.id}/uncomplete`
      : `/api/messages/${message.id}/complete`;

    const res = await fetch(endpoint, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.ok) {
      toast.success(message.completed ? "Marked as Incomplete" : "Marked as Complete");
      await onUpdate();
      onClear();
    } else if (res.status === 401) {
      toast.error("Session expired. Please log in again.");
      localStorage.removeItem("token");
      window.location.reload();
    } else {
      toast.error("Failed to update message");
    }
  };

  const handleNotSpam = async () => {
    if (!token) { toast.error("You must be logged in"); return; }
    const res = await fetch(`/api/messages/${message.id}/not-spam`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.ok) {
      toast.success("Removed from spam");
      await onUpdate();
      onClear();
    } else if (res.status === 401) {
      toast.error("Session expired. Please log in again.");
      localStorage.removeItem("token");
      window.location.reload();
    } else {
      toast.error("Failed to update message");
    }
  };

  const handleMarkAsSpam = async () => {
    if (!token) { toast.error("You must be logged in"); return; }
    if (!window.confirm("Move this message to spam?")) return;

    const res = await fetch(`/api/messages/${message.id}/spam`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.ok) {
      toast.success("Moved to spam");
      await onUpdate();
      onClear();
    } else if (res.status === 401) {
      toast.error("Session expired. Please log in again.");
      localStorage.removeItem("token");
      window.location.reload();
    } else {
      toast.error("Failed to mark as spam");
    }
  };

  const formatDate = (ts) => {
    if (!ts) return null;
    return new Date(ts + "Z").toLocaleString();
  };

  const SOURCE_LABELS = {
    "landing-interior": "Interior Landing",
    "landing-exterior": "Exterior Landing",
  };
  const isLead = message.leadType === "lead";
  const sourceLabel = message.source
    ? (SOURCE_LABELS[message.source] || message.source)
    : "Landing Page";

  return (
    <div className="message-detail">
      {message.spam && (
        <div className="spam-banner">
          <span className="spam-banner__label">⚠ Spam</span>
          {message.spam_reason && (
            <span className="spam-banner__reason">{message.spam_reason}</span>
          )}
        </div>
      )}

      {isLead && !message.spam && (
        <div className="lead-banner">
          <span className="lead-banner__label">🎯 Lead</span>
          <span className="lead-banner__source">{sourceLabel}</span>
        </div>
      )}

      <h2>{message.subject}</h2>

      <div className="meta">
        <p><strong>From:</strong> {message.from}</p>
        {message.phone && <p><strong>Phone:</strong> {message.phone}</p>}
        {message.projectType && <p><strong>Project Type:</strong> {message.projectType}</p>}
        {message.estimateType && <p><strong>Estimate Type:</strong> {message.estimateType}</p>}
        {isLead && message.source && <p><strong>Source:</strong> {sourceLabel}</p>}
        {isLead && message.page && (
          <p><strong>Landing Page:</strong> <a href={message.page} target="_blank" rel="noopener noreferrer">{message.page}</a></p>
        )}
        {message.timestamp && <p><strong>Received:</strong> {formatDate(message.timestamp)}</p>}
      </div>

      <p className="message-body">{message.body}</p>

      {message.images?.length > 0 && (
        <div className="images">
          {message.images.map((path, i) => (
            <div className="image-wrapper" key={i} onClick={() => setSelectedImage(path)}>
              <AuthImage path={path} token={token} />
            </div>
          ))}
        </div>
      )}

      {message.spam ? (
        <button className="not-spam-btn floating" onClick={handleNotSpam}>
          ✉ Not Spam
        </button>
      ) : (
        <div className="action-row floating">
          <button
            className={`complete-btn ${message.completed ? "completed" : ""}`}
            onClick={handleToggleComplete}
          >
            {message.completed ? "↩ Mark as Incomplete" : "✅ Mark as Complete"}
          </button>
          <button className="spam-btn" onClick={handleMarkAsSpam}>
            🚫 Mark as Spam
          </button>
        </div>
      )}


      {selectedImage && (
        <div className="image-overlay" onClick={() => setSelectedImage(null)}>
          <AuthImage path={selectedImage} token={token} />
          <span className="close-btn" onClick={() => setSelectedImage(null)}>×</span>
        </div>
      )}
    </div>
  );
}

export default MessageDetail;
